/**
 * «Dónde está tu dinero» (puro): las capas del gráfico de Tu dinero disponible, mes a mes, y el puente del año.
 * - Disponible: la liquidez de siempre (sin cuentas apartadas).
 * - Apartadas: tu parte de las cuentas apartadas para pagos (p. ej. el piso).
 * - Inversiones: lo aportado (acumulado) y la revalorización (valor − aportado). El valor sale del cierre de Patrimonio de
 *   cada mes; sin cierre, se estima con lo aportado (revalorización 0) y se marca como estimado. Hoy, con los precios actuales.
 * Lo que te deben (Tricount) no es una capa: va aparte, porque no es tuyo hasta que se salda. El puente del año está en year-bridge.ts.
 */
import { investedUntil, monthEnd, prevMonth, round2 } from '@pf/domain';
import { effectiveSnapshots, investments, type Computed } from './model.js';
import type { LiquidityYear } from './liquidity-model.js';

export type LayerKey = 'disp' | 'reserved' | 'invested' | 'gain';
export const LAYERS: Array<{ key: LayerKey; label: string }> = [
  { key: 'disp', label: 'Disponible' }, { key: 'reserved', label: 'Apartadas' },
  { key: 'invested', label: 'Inversiones: aportado' }, { key: 'gain', label: 'Revalorización' },
];
export interface WealthMonth { month: string; live: boolean; disp: number; reserved: number; invested: number; gain: number; estimated: boolean }
export interface WealthLayers {
  months: WealthMonth[];
  /** Al empezar el año (31 dic anterior); null si no se conoce. */
  start: Omit<WealthMonth, 'month' | 'live'> | null;
  /** Nombres de las cuentas apartadas (para la etiqueta de su capa). */
  reservedNames: string[];
}

export function wealthLayers(c: Computed, l: LiquidityYear, today: string): WealthLayers {
  const current = today.slice(0, 7);
  const snaps = new Map(effectiveSnapshots(c).map(s => [s.month, s]));
  const reservedAcc = l.accounts.filter(a => a.reserved);
  const valueNow = investments(c).value;
  const inv = (m: string) => {
    const date = m === current ? today : monthEnd(m);
    const invested = investedUntil(c.wb.flows, date);
    const snap = snaps.get(m);
    const value = m === current && c.wb.positions.length ? valueNow : snap && snap.source !== 'estimated' ? snap.investments : null;
    return { invested: round2(invested), gain: value === null ? 0 : round2(value - invested), estimated: value === null && invested > 0 };
  };
  const months = l.points.map((p): WealthMonth => {
    const idx = l.months.indexOf(p.month);
    const reserved = round2(reservedAcc.reduce((t, a) => t + (a.values[idx] ?? 0), 0));
    return { month: p.month, live: p.live, disp: p.cash, reserved, ...inv(p.month) };
  });
  const first = l.points[0];
  const prevDec = first ? prevMonth(`${first.month.slice(0, 4)}-01`) : '';
  const start = l.start === null || !prevDec ? null
    : { disp: l.start, reserved: round2(reservedAcc.reduce((t, a) => t + (a.start ?? 0), 0)), ...inv(prevDec) };
  return { months, start, reservedNames: reservedAcc.map(a => a.name) };
}

export const layerSum = (m: Omit<WealthMonth, 'month' | 'live'>, on: Set<LayerKey>) =>
  round2((on.has('disp') ? m.disp : 0) + (on.has('reserved') ? m.reserved : 0) + (on.has('invested') ? m.invested : 0) + (on.has('gain') ? m.gain : 0));
